import 'react-datepicker/dist/react-datepicker.css';

import { setHours, setMinutes } from 'date-fns';
import React from 'react';
import { Button, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import { StockSuggestionItem } from '../../../shared/types';
import StockCodeInput from '../../form/StockCodeInput';
import { Direction, InvalidTracker } from '../CreateOrEditTipsModal';
import InfluencerInput from './InfluencerInput';
import SourceLinkInput from './SourceLinkInput';
import {
  DatePickerContainer,
  DirectionButton,
  NonEditableField,
  StyledFormGroup,
  StyledFormLabel,
} from './styledParts';

const FormErrorMessage = ({
  shouldShow,
  error,
}: {
  shouldShow: boolean;
  error: string;
}) => {
  return shouldShow ? (
    <Form.Control.Feedback style={{ display: 'block' }} type="invalid">
      {error}
    </Form.Control.Feedback>
  ) : null;
};

interface FormProps {
  onFormSubmit: (e) => void;
  influencer: string;
  setInfluencer: React.Dispatch<React.SetStateAction<string>>;
  stockCode: string;
  setStockCode: React.Dispatch<React.SetStateAction<string>>;
  setStock: React.Dispatch<React.SetStateAction<StockSuggestionItem>>;
  isStockCodeFromSuggestion: boolean;
  setIsStockCodeFromSuggestion: React.Dispatch<React.SetStateAction<boolean>>;
  stockPrice: number;
  direction: Direction | undefined;
  setDirection: React.Dispatch<React.SetStateAction<Direction>>;
  date: Date;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  isToday: boolean;
  now: Date;
  sourceLink: string | undefined;
  setSourceLink: React.Dispatch<React.SetStateAction<string>>;
  channel: string;
  setChannel: React.Dispatch<React.SetStateAction<string>>;
  keyComment: string;
  setKeyComment: React.Dispatch<React.SetStateAction<string>>;
  invalid: InvalidTracker;
  onReset: () => void;
  submitDisabled: boolean;
  loadingPointInTimePrice: boolean;
}

const CreateOrEditTipsForm: React.FC<FormProps> = ({
  onFormSubmit,
  influencer,
  setInfluencer,
  stockCode,
  setStockCode,
  setStock,
  isStockCodeFromSuggestion,
  setIsStockCodeFromSuggestion,
  stockPrice,
  direction,
  setDirection,
  date,
  setDate,
  isToday,
  now,
  sourceLink,
  setSourceLink,
  channel,
  setChannel,
  keyComment,
  setKeyComment,
  invalid,
  onReset,
  submitDisabled,
  loadingPointInTimePrice,
}) => {
  const [t] = useTranslation();
  const errorPrefix = t('tips-error');
  const errorRequiredPrefix = t('tips-error-required');
  return (
    <Form onSubmit={e => onFormSubmit(e)}>
      <StyledFormGroup className="form-group" controlId="tipsFormInfluencer">
        <StyledFormLabel>{t('influencer')}*</StyledFormLabel>
        <InfluencerInput
          value={influencer}
          onChange={setInfluencer}
          onKeyPress={e => {
            (e.keyCode || e.which) === 13 && e.preventDefault();
          }}
          renderInputComponent={inputProps => {
            return (
              <Form.Control
                {...inputProps}
                type="text"
                placeholder={t('tips-choose-influencer-from-list')}
              />
            );
          }}
        />

        <FormErrorMessage
          shouldShow={invalid.influencer}
          error={`${errorRequiredPrefix} ${t(
            'tips-choose-influencer-from-list'
          )}`}
        />
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormStockCode">
        <StyledFormLabel>{t('stock-code')}*</StyledFormLabel>

        <StockCodeInput
          value={stockCode}
          onChange={setStockCode}
          onChangeStock={setStock}
          isValueProducedBySuggestion={isStockCodeFromSuggestion}
          setIsValueProducedBySuggestion={setIsStockCodeFromSuggestion}
          renderInputComponent={(inputProps: any) => (
            <Form.Control
              {...inputProps}
              type="text"
              placeholder={t('tips-choose-stockCode-from-list')}
              onKeyPress={e => {
                if ((e.keyCode || e.which) === 13) {
                  // Prevent calling the form submit at this point
                  e.preventDefault();
                }
              }}
            />
          )}
        />

        <FormErrorMessage
          shouldShow={invalid.stockCode}
          error={`${errorRequiredPrefix} ${t(
            'tips-choose-stockCode-from-list'
          )}`}
        />
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormDirection">
        <StyledFormLabel>{t('direction')}*</StyledFormLabel>
        <div style={{ display: 'flex' }}>
          <DirectionButton
            isBullish
            isDisable={direction !== 'bullish'}
            onClick={() => setDirection('bullish')}
          >
            {t('tips-bullish')}
          </DirectionButton>

          <DirectionButton
            isDisable={direction !== 'bearish'}
            onClick={() => setDirection('bearish')}
          >
            {t('tips-bearish')}
          </DirectionButton>
        </div>
        <FormErrorMessage
          shouldShow={invalid.direction}
          error={`${errorRequiredPrefix} ${t('tips-choose-direction')}`}
        />
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormPrice">
        <StyledFormLabel>{t('tips-price')}</StyledFormLabel>
        <NonEditableField id="tipsFormPrice">
          {loadingPointInTimePrice ? t('rt-loadingText') : stockPrice}
        </NonEditableField>
      </StyledFormGroup>

      <StyledFormGroup
        className="form-group"
        controlId="tipsFormPredictionDate"
      >
        <StyledFormLabel>{t('tips-date')}</StyledFormLabel>

        <DatePickerContainer>
          <DatePicker
            selected={date}
            onChange={date => setDate(date)}
            timeInputLabel="Time:"
            dateFormat="MM/dd/yyyy h:mm aa"
            showTimeSelect
            timeIntervals={1}
            minTime={isToday ? setHours(setMinutes(now, 0), 0) : undefined}
            maxTime={
              isToday
                ? setHours(setMinutes(now, now.getMinutes()), now.getHours())
                : undefined
            }
            maxDate={now} // TODO: max date should be in Hong Kong Time
          />
          <FormErrorMessage
            shouldShow={invalid.predictionDate}
            error={`${errorPrefix} ${t('tips-no-future-date')}`}
          />
        </DatePickerContainer>
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormChannel">
        <StyledFormLabel>{`${t('channel')}${
          !sourceLink || sourceLink.length === 0 ? '*' : ''
        }`}</StyledFormLabel>
        <Form.Control
          type="text"
          placeholder={t('tips-insert-channel')}
          value={channel}
          onChange={e => setChannel(e.target.value)}
        />
        <FormErrorMessage
          shouldShow={invalid.channel}
          error={`${errorPrefix} ${t('tips-insert-channel')}`}
        />
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormSourceLink">
        <StyledFormLabel>{`${t('source-link')}${
          !channel || channel.length === 0 ? '*' : ''
        }`}</StyledFormLabel>
        <SourceLinkInput
          value={sourceLink}
          onChange={setSourceLink}
          onKeyPress={e => {
            (e.keyCode || e.which) === 13 && e.preventDefault();
          }}
          renderInputComponent={(inputProps: any) => {
            return (
              <Form.Control
                {...inputProps}
                type="text"
                placeholder={t('tips-insert-link')}
                onChange={e => setSourceLink(e.target.value)}
              />
            );
          }}
        />
        <FormErrorMessage
          shouldShow={invalid.sourceLink}
          error={`${errorPrefix} ${t('tips-insert-link')}`}
        />
      </StyledFormGroup>

      <StyledFormGroup className="form-group" controlId="tipsFormkeyComment">
        <StyledFormLabel>{t('key-comment')}</StyledFormLabel>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder={t('tips-insert-keyword')}
          value={keyComment}
          onChange={e => {
            setKeyComment(e.target.value);
          }}
        />
      </StyledFormGroup>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="primary" type="submit" disabled={submitDisabled}>
          {t('tips-submit')}
        </Button>
        <Button
          style={{ marginLeft: 10 }}
          variant="secondary"
          onClick={onReset}
        >
          {t('tips-reset')}
        </Button>
      </div>
    </Form>
  );
};

export default CreateOrEditTipsForm;
